<template>
  <ViewWrapper class="company-detail" footer-orientation="vertical">
    <template #header>
      <div class="company-detail__usercount">
        <AnimatedNumber class="company-detail__usercount-value" el="div" :value="userCount" />
        <div class="company-detail__usercount-label">
          {{ $tc('game.joinedUsers', userCount) }}
        </div>
      </div>
    </template>

    <template #footer>
      <transition name="fade" mode="out-in">
        <div v-if="showDetailLayer" class="company-detail__single_stats">
          <ButtonClose class="company-detail__single-stats-close" @click="hidePinData" />

          <div class="company-detail__info">
            <h3>{{ $t('view.companyDetail.player', { user: userModel.userId }) }}</h3>
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <p>{{ $t('view.companyDetail.location') }}: {{ location.lat }} / {{ location.long }}</p>
          </div>
          <RadarChart
            v-show="currentPinId.id !== undefined"
            :key="`radar-chart-user-${currentPinId.id}`"
            class="company-detail__single_chart"
            :kpi-data="kpiData"
            :ppp-active-sections="pppActiveSections"
            :show-sections="false"
            :scalable="true"
          />
          <HorizontalFocusDistribution
            class="company-detail__single_distribution"
            :distribution="focusDistribution"
          />
        </div>
        <div v-else>
          <InteractionHint
            class="company-detail__interaction-hint"
            :label="$t('ui.interactionHint.zoomInClickAndExplore')"
            icon="rotate"
            animation-class="moveLeftRight"
          />

          <ButtonPill class="company-detail__next" :to="{ name: 'summary' }" replace>
            {{ $t('ui.complete') }}
          </ButtonPill>
        </div>
      </transition>
    </template>
  </ViewWrapper>
</template>

<script>
import store, { getFocusDistributionFromUserModel } from '../store';

import { pausePlayer, playSound } from '../services/sound';

import AnimatedNumber from '../components/AnimatedNumber';
import ButtonClose from '../components/ButtonClose';
import ButtonPill from '../components/ButtonPill';
import HorizontalFocusDistribution from '../components/Chart/HorizontalFocusDistribution';
import InteractionHint from '../components/InteractionHint';
import RadarChart from '../components/radarChart/RadarChart';
import ViewWrapper from '../components/ViewWrapper';

import { pppSections } from '../store/trivia';

export default {
  name: 'CompanyDetail',

  components: {
    AnimatedNumber,
    ButtonClose,
    ButtonPill,
    InteractionHint,
    HorizontalFocusDistribution,
    RadarChart,
    ViewWrapper,
  },

  computed: {
    userCount: () => store.userCount,

    userModel() {
      if (!this.currentPinId) return;
      return store.getDataOfSingleUserById(this.currentPinId);
    },

    location() {
      const [lat, long] = this.userModel.userLocation.split('_');

      return {
        lat: parseFloat(lat).toFixed(3),
        long: parseFloat(long).toFixed(3),
      };
    },

    focusDistribution() {
      if (!this.userModel) return;
      return getFocusDistributionFromUserModel(this.userModel);
    },

    kpiData() {
      if (!this.userModel) return [];
      return this.userModel.kpiRadarChartData;
    },

    currentPinId: () => store.selectedPin,

    pppActiveSections: () => Object.values(pppSections),

    showDetailLayer: () => store.selectedPin !== 0,

    isUserPinSelected: () => store.isUserPinSelected,
  },

  watch: {
    isUserPinSelected(isUserPinSelected) {
      if (!isUserPinSelected) return;

      this.$router.replace('personalDashboard');

      // Reset immediately to be able to get triggered another time
      store.setIsUserPinSelected(false);
    },
  },

  mounted() {
    store.userCount += 1;
    window.scrollTo(0, 0);

    pausePlayer('fxA');
    setTimeout(() => {
      playSound('fxA', 'user');
    }, 0.5);
  },

  methods: {
    hidePinData() {
      store.selectedPin = 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.company-detail__usercount {
  margin-top: 40px;
  max-width: 320px;
  text-align: center;
}

.company-detail__usercount-value {
  font-size: 35px;
  font-weight: bold;
}

.company-detail__usercount-label {
  font-size: 25px;
  font-weight: normal;
}

$item-padding: 20px;
.company-detail__single_stats {
  position: relative;
  width: 100%;
  max-width: $mobileDefaultMaxWidth;
  margin-bottom: 20px;

  margin-left: $item-padding;
  margin-right: $item-padding;
  border-radius: $border-radius;
  background-color: rgba($color-background, 0.9);

  display: grid;

  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto;

  // blur background
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.company-detail__info {
  padding: 20px 20px 0;
  grid-column: 1 / span 2;

  h3 {
    font-size: inherit;
    margin-bottom: 10px;
  }

  p {
    @include font-small;
  }
}

.company-detail__single_chart {
  margin-top: 25px;
}

.company-detail__single_distribution {
  margin: 20px;
  grid-column: 2;
}

.company-detail__single-stats-close {
  position: absolute;
  right: 0;
  top: 0;
  z-index: 100;
}

.company-detail__interaction-hint {
  margin-bottom: calc(20px + env(safe-area-inset-bottom));
}

.company-detail__next {
  margin-bottom: calc(30px + env(safe-area-inset-bottom));
}
</style>
